import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Header from './Header';
import Footer from './Footer';
import SEOHead from './SEOHead';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, LogIn, Users, TrendingUp } from 'lucide-react';
import { affiliateAPI } from '../services/affiliateApi';

const AffiliateLoginPage = () => {
  const { currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const t = (en, es, ru) => currentLanguage === 'es' ? es : currentLanguage === 'ru' ? ru : en;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (forgotMode) {
        await affiliateAPI.forgotPassword(email);
        setForgotSent(true);
      } else {
        await affiliateAPI.login(email, password);
        navigate('/affiliate/dashboard');
      }
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-capsule-bg">
      <SEOHead page="affiliate" />
      <Header />
      <main className="container mx-auto px-4 py-16 md:py-24" data-testid="affiliate-login-page">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-capsule-teal to-capsule-aqua mb-4 shadow-teal-glow">
              <Users className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
              {t('Affiliate Portal', 'Portal de Afiliados', 'Партнерский Портал')}
            </h1>
            <p className="text-gray-600">
              {t('Sign in to track your sales and commissions',
                 'Inicie sesión para ver sus ventas y comisiones',
                 'Войдите, чтобы отслеживать продажи и комиссии')}
            </p>
          </div>

          <Card className="capsule-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <LogIn className="w-5 h-5 text-capsule-teal" />
                {forgotMode
                  ? t('Reset Password', 'Restablecer Contraseña', 'Сброс пароля')
                  : t('Sign In', 'Iniciar Sesión', 'Войти')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {forgotSent ? (
                <div className="text-center py-6">
                  <p className="text-gray-700 mb-4">
                    {t('If an affiliate account exists, a reset link has been sent.',
                       'Si existe una cuenta, se envió un enlace de restablecimiento.',
                       'Если аккаунт существует, ссылка для сброса отправлена.')}
                  </p>
                  <Button onClick={() => { setForgotMode(false); setForgotSent(false); }}
                    className="bg-capsule-teal hover:bg-capsule-teal-dark text-white"
                    data-testid="back-to-login-btn">
                    {t('Back to Login', 'Volver al Inicio de Sesión', 'Вернуться')}
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="email">{t('Email', 'Email', 'Эл. почта')}</Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid="affiliate-login-email"
                      className="mt-1"
                    />
                  </div>
                  {!forgotMode && (
                    <div>
                      <Label htmlFor="password">{t('Password', 'Contraseña', 'Пароль')}</Label>
                      <Input
                        id="password"
                        type="password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        data-testid="affiliate-login-password"
                        className="mt-1"
                      />
                    </div>
                  )}
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-md" data-testid="affiliate-login-error">
                      {error}
                    </div>
                  )}
                  <Button type="submit" disabled={loading}
                    className="w-full bg-gradient-to-r from-capsule-teal to-capsule-aqua hover:from-capsule-teal-dark hover:to-capsule-teal text-white font-semibold py-2.5 rounded-2xl"
                    data-testid="affiliate-login-submit">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : (forgotMode
                      ? t('Send Reset Link', 'Enviar Enlace', 'Отправить ссылку')
                      : t('Sign In', 'Iniciar Sesión', 'Войти'))}
                  </Button>
                  <div className="flex items-center justify-between text-sm">
                    <button type="button"
                      onClick={() => { setForgotMode(!forgotMode); setError(''); }}
                      className="text-capsule-teal hover:underline"
                      data-testid="affiliate-forgot-btn">
                      {forgotMode
                        ? t('Back to login', 'Volver', 'Назад')
                        : t('Forgot password?', '¿Contraseña olvidada?', 'Забыли пароль?')}
                    </button>
                    <Link to="/affiliates" className="text-capsule-teal hover:underline">
                      {t('Apply now', 'Aplicar', 'Подать заявку')} →
                    </Link>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>

          <div className="mt-6 text-center text-sm text-gray-500 flex items-center justify-center gap-2">
            <TrendingUp className="w-4 h-4 text-capsule-teal" />
            {t('Real-time sales tracking · 90-day cookie attribution',
               'Seguimiento en tiempo real · Cookie de 90 días',
               'Отслеживание в реальном времени · Cookie 90 дней')}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AffiliateLoginPage;
